/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useMPSBusinessProfile } from "../../../context/MPSBusinessProfileContext";
import ImageUploader from "../../ui/ImageUploader";
import { getFunctions, httpsCallable } from "firebase/functions";

const BusinessInfoFormPage2 = ({ handleSkip, handleMPSProfileSetupComplete }) => {
  const { user } = useAuth();
  const { MPSBusinessProfileData, updateMPSBusinessProfile } = useMPSBusinessProfile();

  const [companyLogo, setCompanyLogo] = useState(MPSBusinessProfileData.companyLogo || null);
  const [businessDescription, setBusinessDescription] = useState(MPSBusinessProfileData.businessDescription || "");
  const [businessWebsite, setBusinessWebsite] = useState(MPSBusinessProfileData.businessWebsite || "");
  const [businessInstagramAccount, setBusinessInstagramAccount] = useState(
    MPSBusinessProfileData.businessSocialProfiles?.businessInstagramAccount || ""
  );
  const [businessFacebookAccount, setBusinessFacebookAccount] = useState(
    MPSBusinessProfileData.businessSocialProfiles?.businessFacebookAccount || ""
  );
  const [businessTikTokAccount, setBusinessTikTokAccount] = useState(
    MPSBusinessProfileData.businessSocialProfiles?.businessTikTokAccount || ""
  );

  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [saving, setSaving] = useState(false);

  const generateAIDescription = async () => {
    const { businessName, businessType } = MPSBusinessProfileData;
    if (!businessName || !businessType) return;
    setAiLoading(true);
    setAiSuggestion("");
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "aiGenerateBusinessDescription");
      const result = await fn({ businessName, businessType });
      if (result.data?.success) setAiSuggestion(result.data.description);
    } catch (err) {
      console.error("AI error:", err);
    } finally {
      setAiLoading(false);
    }
  };

  const saveAndFinish = async () => {
    setSaving(true);
    updateMPSBusinessProfile({
      companyLogo,
      companyLogoURL: companyLogo,
      businessDescription,
      businessWebsite,
      businessSocialProfiles: {
        businessInstagramAccount,
        businessFacebookAccount,
        businessTikTokAccount,
      },
    });
    await handleMPSProfileSetupComplete();
    setSaving(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-grey-900 mb-2">Polish your profile</h1>
        <p className="text-grey-500 text-sm">
          A strong profile gets approved faster. You can always add this later.
        </p>
      </div>

      <div className="space-y-6">
        {/* Logo */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-grey-900">Business Logo</label>
          <ImageUploader
            variant="logo"
            value={companyLogo}
            onChange={setCompanyLogo}
            helperText="Square logo works best (PNG, JPG, WebP)"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-grey-900">About Your Business</label>
          <textarea
            value={businessDescription}
            onChange={e => setBusinessDescription(e.target.value)}
            placeholder="Describe what you sell and what makes you unique..."
            rows={3}
            className="w-full px-4 py-3 border border-grey-300 rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition resize-none"
          />
          {/* AI writer */}
          <button
            type="button"
            onClick={generateAIDescription}
            disabled={aiLoading || !MPSBusinessProfileData.businessName}
            className="flex items-center gap-2 text-xs text-violet-600 hover:text-violet-700 disabled:text-grey-400 transition font-medium"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            {aiLoading ? "Writing..." : "Write with AI"}
          </button>
          {aiSuggestion && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-sm text-grey-700">
              <p className="mb-2">{aiSuggestion}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { setBusinessDescription(aiSuggestion); setAiSuggestion(""); }}
                  className="px-3 py-1 text-xs bg-grey-900 text-white rounded-full font-medium hover:bg-grey-800"
                >
                  Use this
                </button>
                <button onClick={() => setAiSuggestion("")} className="px-3 py-1 text-xs text-grey-500 hover:text-grey-700">
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Website */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-grey-900">Website</label>
          <input
            type="text"
            value={businessWebsite}
            onChange={e => setBusinessWebsite(e.target.value)}
            placeholder="https://yourbusiness.com"
            className="w-full px-4 py-3 border border-grey-300 rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
          />
        </div>

        {/* Social media */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-grey-900">Social Media</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-400 text-sm">@</span>
              <input
                type="text"
                value={businessInstagramAccount}
                onChange={e => setBusinessInstagramAccount(e.target.value)}
                placeholder="Instagram"
                className="w-full pl-7 pr-3 py-3 border border-grey-300 rounded-xl text-sm text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
              />
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-400 text-sm">@</span>
              <input
                type="text"
                value={businessTikTokAccount}
                onChange={e => setBusinessTikTokAccount(e.target.value)}
                placeholder="TikTok"
                className="w-full pl-7 pr-3 py-3 border border-grey-300 rounded-xl text-sm text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
              />
            </div>
            <input
              type="text"
              value={businessFacebookAccount}
              onChange={e => setBusinessFacebookAccount(e.target.value)}
              placeholder="Facebook URL"
              className="w-full px-4 py-3 border border-grey-300 rounded-xl text-sm text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end pt-2">
          <button
            onClick={saveAndFinish}
            disabled={saving}
            className="px-8 py-3 bg-grey-900 text-white rounded-xl font-semibold text-sm hover:bg-grey-800 disabled:opacity-50 transition"
          >
            {saving ? "Finishing..." : "Finish Setup"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoFormPage2;
